import { useCurrentDate } from "@sellar-io/components";
import {
  CourierDeliveryScheduleListResponseType,
  FulfillmentTypeListResponseType,
  SupplierGetResponseType,
} from "@sellar-io/api-v2/types";
import {
  daysOfWeek,
  findLatestPreOrderAvailableFromDate,
  getNextCourierDispatchAndDeliveryDates,
  getNextStartDate,
} from "@sellar-io/common";
import dayjs from "dayjs";
import { useCourierDeliveryScheduleList } from "../../hooks/api/v2/courierDeliverySchedule/useCourierDeliveryScheduleList";
import { CourierDeliveryScheduleOrderByContent } from "../courierDeliverySchedule/CourierDeliveryScheduleOrderByContent";
import { DeliveryOptionsCardCompact } from "./DeliveryOptionsCardCompact";

export type DeliveryOrderItemType = {
  Product: { availableFrom: Date | null; id: number };
}[];
export const CourierDeliveryOptionsCard = ({
  supplier,
  courierFulfillmentType,
  orderItems,
}: {
  supplier: SupplierGetResponseType;
  courierFulfillmentType:
    | Omit<
        FulfillmentTypeListResponseType[number],
        | "id"
        | "name"
        | "OrderRules"
        | "numberOfCustomers"
        | "numberOfZones"
        | "DeliveryMethodContainerRules"
      >
    | undefined;
  orderItems?: DeliveryOrderItemType;
}) => {
  return (
    <DeliveryOptionsCardCompact
      supplierId={supplier.id}
      description={
        <CourierDeliveryContent supplier={supplier} orderItems={orderItems} />
      }
      fulfillmentType={courierFulfillmentType}
      title={
        <p>
          Delivered by <strong>Courier</strong>
        </p>
      }
    />
  );
};

const CourierDeliveryContent = ({
  supplier,
  orderItems,
}: {
  supplier: SupplierGetResponseType;
  orderItems?: DeliveryOrderItemType;
}) => {
  const courierDeliveryScheduleQuery = useCourierDeliveryScheduleList({
    query: [
      {
        value: supplier.id,
        type: "SupplierId",
        modifier: "EQUALS",
      },
    ],
    sortBy: "createdAt",
    sortDir: "desc",
  });

  const courierDeliverySchedule =
    courierDeliveryScheduleQuery.data?.pages[0]?.[0];

  if (courierDeliveryScheduleQuery.isPending) return null;

  if (!courierDeliverySchedule)
    return (
      <p className={"flex flex-wrap"}>
        Typically delivered in 1-3 business days from dispatch
      </p>
    );

  return (
    <CourierDeliveryDescriptionCompact
      courierDeliverySchedule={courierDeliverySchedule}
      supplier={supplier}
      orderItems={orderItems}
    />
  );
};

const CourierDeliveryDescriptionCompact = ({
  courierDeliverySchedule,
  supplier,
  orderItems,
}: {
  courierDeliverySchedule: CourierDeliveryScheduleListResponseType[number];
  supplier: SupplierGetResponseType;
  orderItems?: DeliveryOrderItemType;
}) => {
  const currentDate = useCurrentDate();
  const latestAvailablePreOrderFromDate = findLatestPreOrderAvailableFromDate(
    orderItems?.map((item) => ({
      availableFrom: item.Product.availableFrom,
    })) ?? [],
  );
  const courierDeliveryCutoffTime = dayjs(
    courierDeliverySchedule.orderCutOffTime,
  )
    .utc()
    .format("HH:mm");

  const outOfOfficeReturnDate = supplier.SupplierOutOfOffice?.outOfOffice
    ? supplier.SupplierOutOfOffice.returnDate
    : null;
  const startDateReference = getNextStartDate({
    latestAvailablePreOrderFromDate,
    outOfOfficeReturnDate,
    courierDeliveryScheduleStartDate: courierDeliverySchedule.startAt,
    currentDate,
  });
  const response = getNextCourierDispatchAndDeliveryDates({
    orderCutOffTime: courierDeliveryCutoffTime,
    courierDaysSchedule: courierDeliverySchedule.CourierDeliveryScheduleDay.map(
      (CourierDeliveryScheduleDay) => CourierDeliveryScheduleDay.day,
    ).map((day: daysOfWeek) => ({ day })),
    dispatchDaysAfterConfirmation:
      courierDeliverySchedule.dispatchDaysAfterConfirmation,
    deliveryDaysAfterDispatch:
      courierDeliverySchedule.deliveryDaysAfterDispatch,
    startDateReference: startDateReference,
    allowSaturdayDelivery: courierDeliverySchedule.allowSaturdayDelivery,
  });

  if (!response) return "";
  return (
    <div className={"flex flex-col"}>
      <p>
        Get it by{" "}
        <strong>{response.estimatedDeliveryDate.format("ddd D MMM")}</strong>
      </p>
      <CourierDeliveryScheduleOrderByContent
        courierDeliveryScheduleEstimations={response}
        courierDeliverySchedule={courierDeliverySchedule}
      />
    </div>
  );
};
